/**
 * « Mes validations » — le VALIDEUR (compte VPC désigné, de n'importe quel service) lit
 * l'arrêté, le corrige lui-même s'il le faut, et décide (Henrico, 28/09) :
 *  · Valider (rien à changer) ;
 *  · Corriger et valider (le texte change ; explication obligatoire ; le rédacteur est
 *    mis au courant de l'avant/après) ;
 *  · Renvoyer au rédacteur (trop à reprendre : retour en brouillon, commentaire obligatoire).
 * Accès limité à SES validations (le serveur le vérifie).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, FileDown, PencilLine, Undo2 } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';

type Ligne = Awaited<ReturnType<typeof api.relecture.mesValidations>>['items'][number];
type Acte = Awaited<ReturnType<typeof api.relecture.acte>>;
type Brouillon = Pick<Acte, 'titre' | 'objet' | 'considerants' | 'articles' | 'corps' | 'effetDu' | 'effetAu'>;
const DECISION: Record<string, string> = { favorable: 'Validé', reserves: 'Avec réserves', defavorable: 'Renvoyé au rédacteur' };
const champ = 'mt-1 w-full rounded-r-md border border-border bg-surface/60 px-3 py-2 text-sm text-ink';

export function MesValidationsPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<Ligne[]>([]);
  const [acte, setActe] = useState<Acte | null>(null);
  const [texte, setTexte] = useState<Brouillon | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const [occupe, setOccupe] = useState(false);
  const choisi = params.get('v');

  const charger = useCallback(async () => {
    try { setItems((await api.relecture.mesValidations()).items); } catch (e) { setMsg({ ok: false, t: e instanceof Error ? e.message : 'Chargement impossible' }); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);
  useEffect(() => {
    setActe(null); setTexte(null); setCommentaire('');
    if (!choisi) return;
    api.relecture.acte(choisi).then((a) => { setActe(a); setTexte({ titre: a.titre, objet: a.objet, considerants: a.considerants, articles: a.articles, corps: a.corps, effetDu: a.effetDu, effetAu: a.effetAu }); })
      .catch((e) => setMsg({ ok: false, t: e instanceof Error ? e.message : 'Arrêté introuvable' }));
  }, [choisi]);

  // Les champs réellement modifiés (seuls envoyés au serveur).
  const modifs = useMemo(() => {
    if (!acte || !texte) return {} as Partial<Brouillon>;
    const out: Partial<Brouillon> = {};
    (Object.keys(texte) as Array<keyof Brouillon>).forEach((k) => {
      if (JSON.stringify(texte[k]) !== JSON.stringify(acte[k])) (out as Record<string, unknown>)[k] = texte[k];
    });
    return out;
  }, [acte, texte]);
  const aModifie = Object.keys(modifs).length > 0;
  const commentaireOk = commentaire.trim().length >= 5;

  const agir = async (f: () => Promise<unknown>, t: string) => {
    setOccupe(true); setMsg(null);
    try { await f(); setMsg({ ok: true, t }); setParams({}); await charger(); }
    catch (e) { setMsg({ ok: false, t: e instanceof Error ? e.message : 'Action impossible' }); }
    finally { setOccupe(false); }
  };
  const lignes = [...items].sort((a, b) => Number(!!a.responduAt) - Number(!!b.responduAt));

  return (
    <div className="min-h-screen">
      <AppHeader module="Mes validations" accent="tech" />
      <main id="main-content" className="mx-auto grid max-w-6xl gap-6 px-4 py-6 lg:grid-cols-[20rem_1fr]">
        <section aria-label="Mes validations">
          <h1 className="font-display text-xl font-semibold text-ink">Mes validations</h1>
          <p className="mt-1 text-sm text-ink-muted">Les arrêtés qu’on vous demande de relire et de valider.</p>
          {!lignes.length && <p className="mt-4 text-sm text-ink-muted">Rien à valider pour l’instant.</p>}
          <ul className="mt-4 space-y-2">
            {lignes.map((l) => (
              <li key={l.intervenantId}>
                <button type="button" onClick={() => setParams({ v: l.intervenantId })}
                  className={`w-full rounded-r-lg border p-3 text-left text-sm ${choisi === l.intervenantId ? 'border-accent bg-surface-hi' : 'border-border bg-surface/55'}`}>
                  <span className="block font-medium text-ink">{l.numero} — {l.titre}</span>
                  <span className="mt-1 block text-xs text-ink-muted">
                    {l.responduAt ? `${DECISION[l.avis ?? ''] ?? 'Répondu'} le ${new Date(l.responduAt).toLocaleDateString('fr-FR')}` : 'À valider'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section aria-label="Arrêté à valider" className="min-w-0 space-y-4">
          {msg && <Alert variant={msg.ok ? 'success' : 'error'}>{msg.t}</Alert>}
          {!choisi && <p className="text-sm text-ink-muted">Choisissez un arrêté dans la liste.</p>}
          {acte && texte && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-display text-lg font-semibold text-ink">Arrêté {acte.numero}</h2>
                <Button type="button" variant="ghost" size="sm" onClick={() => void api.relecture.telechargerPdf(choisi!, acte.numero)}>
                  <FileDown className="h-4 w-4" aria-hidden="true" /> Télécharger le PDF
                </Button>
              </div>
              {!acte.peutCorriger && (
                <Alert variant="info">{acte.dejaRepondu ? 'Vous avez déjà rendu votre décision.' : 'Cet arrêté n’est plus en relecture.'}</Alert>
              )}
              <fieldset disabled={!acte.peutCorriger || occupe} className="space-y-3 rounded-r-xl border border-border bg-surface/55 p-4">
                <legend className="px-1 text-sm font-semibold text-ink">Texte de l’arrêté — modifiable si vous le corrigez</legend>
                <label className="block text-sm text-ink">Titre
                  <input className={champ} value={texte.titre} onChange={(e) => setTexte({ ...texte, titre: e.target.value })} /></label>
                <label className="block text-sm text-ink">Objet
                  <textarea className={champ} rows={2} value={texte.objet ?? ''} onChange={(e) => setTexte({ ...texte, objet: e.target.value || null })} /></label>
                <label className="block text-sm text-ink">Considérants (un par ligne)
                  <textarea className={champ} rows={4} value={texte.considerants.join('\n')}
                    onChange={(e) => setTexte({ ...texte, considerants: e.target.value.split('\n').filter((x) => x.trim()) })} /></label>
                {texte.articles.map((a, i) => (
                  <label key={a.numero} className="block text-sm text-ink">Article {a.numero}
                    <textarea className={champ} rows={3} value={a.texte}
                      onChange={(e) => setTexte({ ...texte, articles: texte.articles.map((x, j) => (j === i ? { ...x, texte: e.target.value } : x)) })} /></label>
                ))}
                {texte.corps !== null && (
                  <label className="block text-sm text-ink">Texte de l’arrêté
                    <textarea className={champ} rows={10} value={texte.corps} onChange={(e) => setTexte({ ...texte, corps: e.target.value })} /></label>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm text-ink">Effet à partir du
                    <input type="date" className={champ} value={texte.effetDu ?? ''} onChange={(e) => setTexte({ ...texte, effetDu: e.target.value || null })} /></label>
                  <label className="block text-sm text-ink">Effet jusqu’au
                    <input type="date" className={champ} value={texte.effetAu ?? ''} onChange={(e) => setTexte({ ...texte, effetAu: e.target.value || null })} /></label>
                </div>
              </fieldset>

              {acte.peutCorriger && (
                <div className="space-y-3 rounded-r-xl border border-border bg-surface/55 p-4">
                  <label className="block text-sm font-medium text-ink">
                    {aModifie ? 'Ce que vous avez corrigé et pourquoi (le rédacteur le recevra)' : 'Commentaire (obligatoire pour renvoyer au rédacteur)'}
                    <textarea className={champ} rows={3} maxLength={2000} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {!aModifie ? (
                      <Button type="button" disabled={occupe} onClick={() => void agir(() => api.relecture.avis(choisi!, 'favorable', commentaire.trim() || null), 'Arrêté validé.')}>
                        <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Valider
                      </Button>
                    ) : (
                      <Button type="button" disabled={occupe || !commentaireOk}
                        onClick={() => void agir(() => api.relecture.corrigerEtValider(choisi!, { ...modifs, commentaire: commentaire.trim() }), 'Arrêté corrigé et validé : le rédacteur est prévenu de vos corrections.')}>
                        <PencilLine className="h-4 w-4" aria-hidden="true" /> Corriger et valider
                      </Button>
                    )}
                    <Button type="button" variant="ghost" disabled={occupe || !commentaireOk || aModifie}
                      title={aModifie ? 'Annulez vos modifications, ou choisissez « Corriger et valider »' : undefined}
                      onClick={() => void agir(() => api.relecture.avis(choisi!, 'defavorable', commentaire.trim()), 'Arrêté renvoyé au rédacteur avec votre commentaire.')}>
                      <Undo2 className="h-4 w-4" aria-hidden="true" /> Renvoyer au rédacteur
                    </Button>
                  </div>
                  <p className="text-xs text-ink-muted">Quand tous les valideurs ont validé et que les avis externes sont reçus, l’arrêté part automatiquement à la signature du maire.</p>
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
}
